import { IRowData } from "@multiweb/types/contracts";
import { CultureInfo } from "../../../Global/Dom/CultureInfo";
import { createElement, getScrollBarWidth, registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { IDataGridDataShownEventData } from "../IEvent";
import { CalendarEvent } from "./CalendarEvent";
import { CalendarHourEvent } from "./CalendarHourEvent";
import { DayCalendarGrid } from "./DayCalendarGrid";
import { DayHourCell } from "./DayHourCell";

export class WeekCalendarGrid extends DayCalendarGrid {
    protected override _constructor(): void {
        super._constructor();
        this.classList.add("week");
    }

    public override get dateSpan(): Date {
        const lastDay = new Date(this._currentDate);
        lastDay.setDate(lastDay.getDate() + 7);
        lastDay.setHours(0, 0, 0, 0);

        return lastDay;
    }

    public override clearSelectedRows(): void {
        while (this.selectedRows.length != 0) {
            this.selectedRows.pop().isSelected = false;
        }
    }

    public override initialize(): void {
        if (this._dataSource) {
            this.dataSource.dataLoaded.add((_sender, eventData) => {
                if (!this.visible)
                    return;
                if (eventData.refreshedData)
                    this._refreshRowsWithData(eventData);
                else {
                    this._showData(eventData);
                }
                this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
            });
        }
    }

    public override actualizeDisplayedInformation(_ce: CalendarEvent = null): void {
        // Prázdná metoda
    }

    protected override _createInnerGrid(): HTMLElement {
        this._calendarEvents = new Array<CalendarHourEvent>();
        const grid = createElement({ classes: "weeks-grid" });
        this._dayCells.clear();
        if (this.currentDate == undefined)
            this.currentDate = new Date();

        const startDate = new Date(this.currentDate);
        startDate.setHours(0, 0, 0, 0);

        for (let x = 0; x < 24; x++) {
            const lineGrid = createElement({ classes: "week-row" });
            const hourElement = createElement({ classes: "week-hour" });
            hourElement.textContent = x.toString() + ":00";
            lineGrid.appendChild(hourElement);

            lineGrid.setAttribute("data-hour", x.toString());

            for (let d = 0; d < 7; d++) {
                const dayCell = new DayHourCell();
                dayCell.idOfCell = new Date(startDate);
                this._dayCells.set(dayCell.hourId, dayCell);
                lineGrid.appendChild(dayCell);

                dayCell.addEventListener("click", (_e: MouseEvent) => {
                    if (this.selectedCell)
                        this.selectedCell.classList.remove("selected");
                    this._selectedCell = dayCell;
                    this.selectedCell.classList.add("selected");
                });

                startDate.setDate(startDate.getDate() + 1);
                startDate.setHours(x); //kvůli změně času
                grid.appendChild(lineGrid);

                dayCell.eventAdded.add((md, _cvEvent) => {
                    for (const event of this._calendarEvents.reverse())
                        this._updateEventPosition(event, md);
                });
            }
            startDate.setHours(startDate.getHours() + 1);
            startDate.setDate(startDate.getDate() - 7);
        }

        return grid;
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected override _createEventByDS(data: IRowData, uniqueId: string): CalendarHourEvent {
        const dataRow: CalendarHourEvent = CalendarHourEvent.createEventByIRowData(this, uniqueId, data);

        this.dataRows.push(dataRow);

        return dataRow;
    }

    protected override _createInnerHeaderGrid(): HTMLElement {
        const grid = createElement({ classes: "header-grid" });

        if (this.currentDate == undefined)
            this.currentDate = new Date();

        const startDate = new Date(this.currentDate);
        startDate.setHours(0, 0, 0, 0);
        this._allDayCells.clear();

        const allDayRow = createElement({ classes: "day-row" });
        const hourElement = createElement({ classes: "week-hour" });
        allDayRow.appendChild(hourElement);

        const optionsName: Intl.DateTimeFormatOptions = { weekday: "short" };

        const curCult = CultureInfo.getCultureInfo();
        const curCultName = curCult.name;

        for (let d = 0; d < 7; d++) {
            const allDayCell = new DayHourCell();

            allDayCell.allDayCell = true;
            allDayCell.idOfCell = new Date(startDate);
            allDayCell.visibleDayNumber = true;

            allDayCell.dayNameText = startDate.toLocaleString(curCultName, optionsName);
            allDayCell.dayNumberText = startDate.getDate().toString();

            allDayRow.appendChild(allDayCell);
            this._allDayCells.set(allDayCell.hourId, allDayCell);

            startDate.setDate(startDate.getDate() + 1);
        }
        // dorovnava sirku scrollbaru, aby neodjizdela hlavicka pokud doscroluju horizontalnim scrollbarem nakoneca je zobrazen verticalni scrollbar

        const scrollBarBuffer = createElement({ classes: "week-hour" });
        scrollBarBuffer.style.width = getScrollBarWidth() + "px";
        allDayRow.appendChild(scrollBarBuffer);

        grid.appendChild(allDayRow);

        return grid;
    }

    protected override _renderHeader(): void {
        this._mainListDiv.insertAdjacentElement("afterbegin", this._createInnerHeaderGrid());
    }

    protected override _renderDataContainer(): void {
        this._listDiv = document.createElement("div");

        this._listDiv.classList.add("list-div");
        this._mainListDiv.appendChild(this._listDiv);

        const table = document.createElement("table");

        this._dataTable = document.createElement("tbody");
        this._dataTable.classList.add("table-body");

        table.appendChild(this._dataTable);

        this._listDiv.appendChild(table);

        const innerGrid = this._createInnerGrid();
        this._mainListDiv.insertAdjacentElement("beforeend", innerGrid);

        const firstHourRow = innerGrid.querySelector(`[data-hour='${this._firstHour.toString()}']`) as HTMLElement;
        if (firstHourRow)
            innerGrid.scrollTop = firstHourRow.offsetTop < 20 ? 0 : firstHourRow.offsetTop - 20;
    }
}

registerCustomElement("tsc-WeekCalendarGrid", WeekCalendarGrid);
